import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "timezone-selector-wrapper" }
const _hoisted_2 = ["for"]
const _hoisted_3 = {
  key: 0,
  class: "timezone-selector__required"
}
const _hoisted_4 = ["id"]
const _hoisted_5 = { class: "timezone-selector__trigger-text" }
const _hoisted_6 = {
  key: 0,
  class: "timezone-selector__trigger-time"
}
const _hoisted_7 = { class: "timezone-selector__search" }
const _hoisted_8 = { class: "timezone-selector__header" }
const _hoisted_9 = {
  key: 0,
  class: "timezone-selector__toggle"
}
const _hoisted_10 = { class: "timezone-selector__list" }
const _hoisted_11 = {
  key: 0,
  class: "timezone-selector__empty"
}
const _hoisted_12 = { class: "timezone-selector__group-header" }
const _hoisted_13 = { class: "timezone-selector__group-count" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "timezone-selector__item-main" }
const _hoisted_16 = { class: "timezone-selector__item-name" }
const _hoisted_17 = {
  key: 0,
  class: "timezone-selector__item-cities"
}
const _hoisted_18 = { class: "timezone-selector__item-time" }
const _hoisted_19 = { class: "timezone-selector__footer-text" }

import { Icon, Input } from "@/components/ui";
import { TIMEZONES, type TimezoneOption } from "@/utils/enums";
import { Teleport, computed, onMounted, onUnmounted, ref, watch } from "vue";

interface TimezoneGroup {
  region: string;
  timezones: TimezoneOption[];
}

type TimeFormat = "12h" | "24h";

interface Props {
  modelValue?: string;
  autoSetDefault?: boolean;
  label?: string;
  required?: boolean;
  id?: string;
  showFormatToggle?: boolean;
  format?: TimeFormat;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TimezoneSelector',
  props: {
    modelValue: { default: "" },
    autoSetDefault: { type: Boolean, default: false },
    label: {},
    required: { type: Boolean, default: false },
    id: {},
    showFormatToggle: { type: Boolean, default: false },
    format: { default: "12h" }
  },
  emits: ["update:modelValue", "blur"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const searchQuery = ref("");
const use24Hour = ref(props.format === "24h");
const selectedTimezone = ref(props.modelValue || "");
const isOpen = ref(false);

// Watch for format prop changes when toggle is hidden
watch(
  () => props.format,
  (newFormat) => {
    if (!props.showFormatToggle) {
      use24Hour.value = newFormat === "24h";
    }
  },
  { immediate: true }
);

const timezoneSelectorId = computed(
  () => props.id || `timezone-selector-${Math.random().toString(36).substr(2, 9)}`
);
const dropdownStyle = ref<Record<string, string>>({});
const triggerRef = ref<HTMLElement | null>(null);
let timeUpdateInterval: ReturnType<typeof setInterval> | null = null;

// Group timezones by region
const timezoneGroups = computed((): TimezoneGroup[] => {
  const groups: Record<string, TimezoneOption[]> = {};
  const seenValues = new Set<string>();

  TIMEZONES.forEach((tz) => {
    let region = "OTHER";

    if (tz.value.startsWith("America/") || tz.value.startsWith("Pacific/Honolulu")) {
      if (
        tz.value.includes("New_York") ||
        tz.value.includes("Toronto") ||
        tz.value.includes("Chicago") ||
        tz.value.includes("Denver") ||
        tz.value.includes("Los_Angeles") ||
        tz.value.includes("Vancouver") ||
        tz.value.includes("Anchorage") ||
        tz.value.includes("Honolulu")
      ) {
        region = "US/CANADA";
      } else {
        region = "AMERICA";
      }
    } else if (tz.value.startsWith("Europe/")) {
      region = "EUROPE";
    } else if (tz.value.startsWith("Asia/")) {
      region = "ASIA";
    } else if (tz.value.startsWith("Australia/") || tz.value.startsWith("Pacific/")) {
      region = "AUSTRALIA/PACIFIC";
    } else if (tz.value.startsWith("Africa/")) {
      region = "AFRICA";
    }

    if (!groups[region]) {
      groups[region] = [];
    }

    // Skip duplicates with same IANA value
    if (seenValues.has(tz.value)) {
      return;
    }

    seenValues.add(tz.value);
    groups[region].push(tz);
  });

  // Sort regions and timezones within each region
  const sortedGroups: TimezoneGroup[] = [];
  const regionOrder = [
    "US/CANADA",
    "AMERICA",
    "EUROPE",
    "AFRICA",
    "ASIA",
    "AUSTRALIA/PACIFIC",
    "OTHER",
  ];

  regionOrder.forEach((region) => {
    if (groups[region]) {
      sortedGroups.push({
        region,
        timezones: groups[region],
      });
    }
  });

  return sortedGroups;
});

// Filter timezones based on search query
const filteredTimezoneGroups = computed((): TimezoneGroup[] => {
  if (!searchQuery.value.trim()) {
    return timezoneGroups.value;
  }

  const query = searchQuery.value.toLowerCase();
  const filtered: TimezoneGroup[] = [];

  timezoneGroups.value.forEach((group) => {
    const matchingTimezones = group.timezones.filter(
      (tz) =>
        tz.label.toLowerCase().includes(query) ||
        tz.value.toLowerCase().includes(query) ||
        group.region.toLowerCase().includes(query) ||
        (tz.cities ?? []).some((city) => city.toLowerCase().includes(query))
    );

    if (matchingTimezones.length > 0) {
      filtered.push({
        region: group.region,
        timezones: matchingTimezones,
      });
    }
  });

  return filtered;
});

const getFormattedTime = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: !use24Hour.value,
    });

    return formatter.format(now);
  } catch (error) {
    return "--:--";
  }
};

// Get selected timezone label
const selectedTimezoneLabel = computed(() => {
  const tz = TIMEZONES.find((tz) => tz.value === selectedTimezone.value);
  return tz?.label || "Select Timezone";
});

// Get selected timezone current time
const selectedTimezoneTime = computed(() => {
  if (!selectedTimezone.value) return "--:--";
  return getFormattedTime(selectedTimezone.value);
});

// Select timezone
const selectTimezone = (value: string) => {
  selectedTimezone.value = value;
  emit("update:modelValue", value);
  // Close dropdown after selection
  setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const updateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = 380;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  // Determine if dropdown should appear above or below
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  dropdownStyle.value = {
    position: "fixed",
    top: shouldShowAbove
      ? `${rect.top - dropdownHeight - spacing}px`
      : `${rect.bottom + spacing}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: "10070",
  };
};

// Toggle dropdown
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    setTimeout(() => {
      updateDropdownPosition();
    }, 0);
  }
};

// Handle blur event
const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".timezone-selector-wrapper") &&
    !relatedTarget.closest(".timezone-selector")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      emit("blur", event);
    }, 100);
  }
};

// Close dropdown when clicking outside
let clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

// Update time every minute
const startTimeUpdate = () => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
  }
  timeUpdateInterval = setInterval(() => {
    const temp = searchQuery.value;
    searchQuery.value = temp + " ";
    searchQuery.value = temp;
  }, 60000);
};

// Watch for prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== selectedTimezone.value) {
      selectedTimezone.value = newValue || "";
    }
  },
  { immediate: true }
);

// Emit change when selected timezone updates
watch(selectedTimezone, (newValue) => {
  if (newValue !== props.modelValue) {
    emit("update:modelValue", newValue);
  }
});

onMounted(() => {
  startTimeUpdate();

  // Set default timezone if needed
  if (!selectedTimezone.value && props.autoSetDefault) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzExists = TIMEZONES.find((tz) => tz.value === userTimezone);
    if (tzExists) {
      selectedTimezone.value = userTimezone;
      emit("update:modelValue", userTimezone);
    }
  }

  // Handle clicks outside
  clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      target &&
      !target.closest(".timezone-selector-wrapper") &&
      !target.closest(".timezone-selector")
    ) {
      isOpen.value = false;
    }
  };
  document.addEventListener("click", clickOutsideHandler);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
  }
  if (clickOutsideHandler) {
    document.removeEventListener("click", clickOutsideHandler);
  }
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: timezoneSelectorId.value,
          class: "timezone-selector__label"
        }, [
          _createTextVNode(_toDisplayString(__props.label) + " ", 1),
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      id: timezoneSelectorId.value,
      ref_key: "triggerRef",
      ref: triggerRef,
      class: _normalizeClass(["timezone-selector__trigger", { 'timezone-selector__trigger--open': isOpen.value }]),
      onClick: toggleDropdown,
      onBlur: handleBlur,
      tabindex: "0"
    }, [
      _createVNode(_unref(Icon), {
        icon: "mdi:earth",
        class: "timezone-selector__trigger-globe"
      }),
      _createElementVNode("span", _hoisted_5, _toDisplayString(selectedTimezoneLabel.value || "Select Timezone"), 1),
      (selectedTimezone.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(selectedTimezoneTime.value), 1))
        : _createCommentVNode("", true),
      _createVNode(_unref(Icon), {
        icon: "mdi:chevron-down",
        class: "timezone-selector__trigger-icon"
      })
    ], 42, _hoisted_4),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "timezone-selector",
            style: _normalizeStyle(dropdownStyle.value)
          }, [
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_unref(Input), {
                modelValue: searchQuery.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                type: "text",
                placeholder: "Search...",
                leadingIcon: "mdi:magnify",
                clearable: !!searchQuery.value,
                size: "sm",
                onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
                onMousedown: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                onFocus: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
              }, null, 8, ["modelValue", "clearable"])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _cache[6] || (_cache[6] = _createElementVNode("span", { class: "timezone-selector__header-label" }, "TIME ZONE", -1)),
              (__props.showFormatToggle)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["timezone-selector__toggle-option", { 'timezone-selector__toggle-option--active': !use24Hour.value }])
                    }, " am/pm ", 2),
                    _createElementVNode("button", {
                      class: _normalizeClass(["timezone-selector__toggle-switch", { 'timezone-selector__toggle-switch--active': use24Hour.value }]),
                      onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (use24Hour.value = !use24Hour.value), ["stop"]))
                    }, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("span", { class: "timezone-selector__toggle-slider" }, null, -1)
                    ]))], 2),
                    _createElementVNode("span", {
                      class: _normalizeClass(["timezone-selector__toggle-option", { 'timezone-selector__toggle-option--active': use24Hour.value }])
                    }, " 24h ", 2)
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_10, [
              (filteredTimezoneGroups.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, " No timezones found for \"" + _toDisplayString(searchQuery.value) + "\" ", 1))
                : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(filteredTimezoneGroups.value, (group, groupIndex) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: groupIndex,
                      class: "timezone-selector__group"
                    }, [
                      _createElementVNode("div", _hoisted_12, [
                        _createTextVNode(_toDisplayString(group.region) + " ", 1),
                        _createElementVNode("span", _hoisted_13, _toDisplayString(group.timezones.length), 1)
                      ]),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.timezones, (timezone) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: timezone.value,
                          class: _normalizeClass(["timezone-selector__item", {
                  'timezone-selector__item--selected': selectedTimezone.value === timezone.value,
                }]),
                          onClick: _withModifiers(($event: any) => (selectTimezone(timezone.value)), ["stop"])
                        }, [
                          _createElementVNode("div", _hoisted_15, [
                            _createElementVNode("span", _hoisted_16, _toDisplayString(timezone.label), 1),
                            (timezone.cities?.length && searchQuery.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(timezone.cities.slice(0, 4).join(" · ")), 1))
                              : _createCommentVNode("", true)
                          ]),
                          _createElementVNode("span", _hoisted_18, _toDisplayString(getFormattedTime(timezone.value)), 1)
                        ], 10, _hoisted_14))
                      }), 128))
                    ]))
                  }), 128))
            ]),
            _createElementVNode("div", {
              class: "timezone-selector__footer",
              onClick: _withModifiers(toggleDropdown, ["stop"])
            }, [
              _createVNode(_unref(Icon), {
                icon: "mdi:earth",
                class: "timezone-selector__footer-icon"
              }),
              _createElementVNode("span", _hoisted_19, _toDisplayString(selectedTimezoneLabel.value) + " (" + _toDisplayString(selectedTimezoneTime.value) + ") ", 1),
              _createVNode(_unref(Icon), {
                icon: "mdi:chevron-up",
                class: "timezone-selector__footer-arrow"
              })
            ])
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})